import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Play, Pause, Clock, CheckCircle, Video, Music, FileText, File, ExternalLink } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dimension mapping for pillars
const PILLAR_DIMENSION_MAP = {
  wellness: 'self_care',
  skill: 'rnd_learning',
  recovery: 'self_care'
};

const SessionViewer = ({ user }) => {
  const { programId, sessionId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [completed, setCompleted] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const fetchProgramAndSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const programRes = await axios.get(`${API_URL}/programs/${programId}`, { headers });
      setProgram(programRes.data);

      const currentSession = programRes.data.sessions?.find(s => s.id === sessionId);
      if (currentSession) {
        setSession(currentSession);
      } else {
        toast.error('Session not found');
        navigate(`/dashboard/programs`);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const startTimer = useCallback(() => {
    if (!timerRef.current && !completed) {
      startTimeRef.current = Date.now() - (elapsedTime * 1000);
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
      setIsPlaying(true);
    }
  }, [elapsedTime, completed]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      setIsPlaying(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchProgramAndSession();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [programId, sessionId]); // eslint-disable-line

  // Auto-start timer for text content when page is active
  useEffect(() => {
    if (session?.content_type === 'text' && !completed) {
      startTimer();
      
      const handleVisibilityChange = () => {
        if (document.hidden) {
          pauseTimer();
        } else if (!completed) {
          startTimer();
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        pauseTimer();
      };
    }
  }, [session, completed, startTimer, pauseTimer]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleComplete = async () => {
    try {
      pauseTimer();
      const token = localStorage.getItem('token');
      const dimension = PILLAR_DIMENSION_MAP[program?.pillar] || 'self_care';

      await axios.post(`${API_URL}/programs/session-progress`, {
        program_id: programId,
        session_id: sessionId,
        time_spent_seconds: elapsedTime,
        dimension: dimension
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompleted(true);
      toast.success('Session completed! Time added to your Daily Scan.');
    } catch (error) {
      console.error('Failed to save progress:', error);
      toast.error('Failed to save progress');
    }
  };

  // Handle video play/pause events
  const handleVideoPlay = () => startTimer();
  const handleVideoPause = () => pauseTimer();

  // Handle audio play/pause events
  const handleAudioPlay = () => startTimer();
  const handleAudioPause = () => pauseTimer();

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return <Video size={20} />;
      case 'audio': return <Music size={20} />;
      case 'text': return <FileText size={20} />;
      default: return <File size={20} />;
    }
  };

  // Helper to get embed URL for external video services
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1` : null;
    }
    
    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
    
    return null; // Direct video URL
  };

  const renderContent = () => {
    if (!session) return null;

    switch (session.content_type) {
      case 'video':
        const embedUrl = getVideoEmbedUrl(session.content_url);
        return (
          <div style={{ 
            width: '100%', 
            aspectRatio: '16/9', 
            background: '#000',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {session.content_url ? (
              embedUrl ? (
                <>
                  <iframe
                    width="100%"
                    height="100%"
                    src={embedUrl}
                    title={session.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  {/* Overlay hint for embedded videos */}
                  <div style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '12px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Clock size={14} />
                    Use timer controls on the right →
                  </div>
                </>
              ) : (
                <video
                  ref={videoRef}
                  controls
                  style={{ width: '100%', height: '100%' }}
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onEnded={handleVideoPause}
                >
                  <source src={session.content_url} />
                  Your browser does not support video playback.
                </video>
              )
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: '#888'
              }}>
                No video content available
              </div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div style={{
            padding: '40px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: isPlaying ? 'pulse 2s infinite' : 'none'
            }}>
              <Music size={48} color="white" />
            </div>
            {session.content_url ? (
              <audio
                ref={audioRef}
                controls
                style={{ width: '100%', maxWidth: '400px' }}
                onPlay={handleAudioPlay}
                onPause={handleAudioPause}
                onEnded={handleAudioPause}
              >
                <source src={session.content_url} />
                Your browser does not support audio playback.
              </audio>
            ) : (
              <p style={{ color: 'white' }}>No audio content available</p>
            )}
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', margin: 0 }}>
              ⏱️ Timer runs automatically when audio is playing
            </p>
          </div>
        );

      case 'text':
        return (
          <div style={{
            padding: '32px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            position: 'relative'
          }}>
            {/* Auto-timer indicator for text */}
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: isPlaying ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
              color: isPlaying ? '#10B981' : '#F59E0B',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              {isPlaying ? (
                <>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', animation: 'pulse 1s infinite' }}></span>
                  Timer Running
                </>
              ) : (
                <>
                  <Pause size={12} />
                  Timer Paused
                </>
              )}
            </div>
            <div 
              style={{ 
                lineHeight: '1.8', 
                color: 'var(--text-primary)',
                fontSize: '16px',
                whiteSpace: 'pre-wrap',
                marginTop: '24px'
              }}
              dangerouslySetInnerHTML={{ __html: session.content_text || 'No text content available' }}
            />
          </div>
        );

      case 'document':
        return (
          <div style={{
            padding: '40px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            textAlign: 'center'
          }}>
            <File size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
            {session.content_url ? (
              <>
                <a
                  href={session.content_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={startTimer}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  <ExternalLink size={18} />
                  Open Document
                </a>
                <p style={{ margin: '16px 0 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>
                  Click to open document. Use timer controls on the right.
                </p>
              </>
            ) : (
              <p style={{ color: 'var(--text-tertiary)' }}>No document available</p>
            )}
          </div>
        );

      default:
        return (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center',
            background: 'var(--bg-secondary)',
            borderRadius: '12px'
          }}>
            <p style={{ color: 'var(--text-tertiary)' }}>
              Content type not supported
            </p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!session || !program) {
    return (
      <div className="content-area" style={{ textAlign: 'center', padding: '60px' }}>
        <h2>Session not found</h2>
        <button onClick={() => navigate('/dashboard/programs')} className="gradient-btn" style={{ marginTop: '20px' }}>
          Back to Programs
        </button>
      </div>
    );
  }

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => navigate(`/dashboard/programs/${programId}`)}
          style={{
            padding: '10px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--text-primary)'
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '600' }}>
            {program.title}
          </p>
          <h1 style={{ margin: '4px 0 0 0', fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
            {session.title}
          </h1>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px' }}>
        {/* Content Area */}
        <div>
          {renderContent()}

          {/* Description */}
          {session.description && (
            <div style={{ marginTop: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                About this session
              </h3>
              <p style={{ margin: 0, color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {session.description}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar - Progress Tracker */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Timer Card */}
          <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '16px',
              color: 'var(--text-secondary)'
            }}>
              <Clock size={20} />
              <span style={{ fontSize: '14px', fontWeight: '500' }}>Time Spent</span>
            </div>
            
            <div style={{
              fontSize: '36px',
              fontWeight: '700',
              fontFamily: 'monospace',
              color: isPlaying ? '#10B981' : 'var(--text-primary)',
              marginBottom: '20px',
              transition: 'color 0.3s'
            }}>
              {formatTime(elapsedTime)}
            </div>

            {/* Timer status indicator */}
            <div style={{
              padding: '8px 16px',
              background: isPlaying ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-secondary)',
              borderRadius: '20px',
              marginBottom: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: '600',
              color: isPlaying ? '#10B981' : 'var(--text-tertiary)'
            }}>
              {isPlaying ? (
                <>
                  <span style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: '#10B981',
                    animation: 'pulse 1s infinite'
                  }}></span>
                  Recording...
                </>
              ) : (
                <>
                  <Pause size={14} />
                  Paused
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              {!completed ? (
                <>
                  <button
                    onClick={isPlaying ? pauseTimer : startTimer}
                    style={{
                      padding: '12px 24px',
                      background: isPlaying ? 'var(--bg-secondary)' : 'var(--color-primary)',
                      color: isPlaying ? 'var(--text-primary)' : 'white',
                      border: isPlaying ? '1px solid var(--border-color)' : 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: '600'
                    }}
                  >
                    {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                    {isPlaying ? 'Pause' : 'Start'}
                  </button>
                  <button
                    onClick={handleComplete}
                    disabled={elapsedTime < 10}
                    style={{
                      padding: '12px 24px',
                      background: elapsedTime < 10 ? 'var(--bg-secondary)' : '#10B981',
                      color: elapsedTime < 10 ? 'var(--text-tertiary)' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: elapsedTime < 10 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: '600'
                    }}
                  >
                    <CheckCircle size={18} />
                    Complete
                  </button>
                </>
              ) : (
                <div style={{
                  padding: '16px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#10B981',
                  fontWeight: '600'
                }}>
                  <CheckCircle size={20} />
                  Session Completed!
                </div>
              )}
            </div>
          </div>

          {/* Auto-timer info */}
          <div style={{
            padding: '16px',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: '#3B82F6' }}>
              ⏱️ Auto-Timer
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              {session.content_type === 'video' && 'Timer syncs with video playback (direct videos only). For YouTube/Vimeo, use manual controls.'}
              {session.content_type === 'audio' && 'Timer automatically starts when audio plays and pauses when stopped.'}
              {session.content_type === 'text' && 'Timer runs automatically while you read. Pauses when you switch tabs.'}
              {session.content_type === 'document' && 'Click "Start" to track time while viewing the document.'}
            </p>
          </div>

          {/* Session Info */}
          <div className="card" style={{ padding: '20px' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Session Details
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {getContentIcon(session.content_type)}
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                  {session.content_type} Content
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Clock size={20} style={{ color: 'var(--text-tertiary)' }} />
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Est. {session.duration_minutes} minutes
                </span>
              </div>
            </div>
          </div>

          {/* Time will be added to */}
          <div style={{
            padding: '16px',
            background: 'rgba(124, 58, 237, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(124, 58, 237, 0.2)'
          }}>
            <p style={{ margin: 0, fontSize: '13px', color: '#7C3AED', textAlign: 'center' }}>
              ⏱️ Time will be added to your <strong>{PILLAR_DIMENSION_MAP[program?.pillar] === 'rnd_learning' ? 'R&D/Learning' : 'Self-Care'}</strong> in Daily Scan
            </p>
          </div>
        </div>
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SessionViewer;
